import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { Loader } from '@ophelos/ui/components/loader';
import { Separator } from '@ophelos/ui/components/separator';
import { ArrowLeft, Calendar, Mail, User } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SettingsMenu } from '@/components/ui/SettingsMenu';
import { useAuth } from '@/contexts/customer-auth-context';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';

export default function Account() {
  const { t, currentLanguage } = useTranslation();
  const { accountId, user } = useAuth();
  const { account, isLoading } = useDebtData();
  const navigate = useNavigate();

  // Stage-2 magic-auth claim via user_scope_links is removed — do not offer verification.
  // Show Stage-1 account fields that Connect already returns for access-code sessions.
  const customerName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : localStorage.getItem('customer_name') || '';
  const customerEmail = user?.email || localStorage.getItem('user_email') || '';

  useEffect(() => {
    if (!accountId) {
      navigate('/login');
    }
  }, [accountId, navigate]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      </Layout>
    );
  }

  if (!account) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-muted-foreground">{t('account.accountNotFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="relative max-w-5xl w-full mx-auto">
        <div className="p-4 flex items-center gap-2 max-md:justify-start md:justify-between">
          <Button asChild variant="subtle" size="icon" className="rounded-full max-md:size-8 md:absolute md:top-4 md:left-4">
            <Link to="/dashboard">
              <ArrowLeft />
            </Link>
          </Button>
          <SettingsMenu size="icon-lg" className="md:ml-auto" />
        </div>

        <div className="w-full max-w-lg mx-auto px-4 pt-2 lg:pt-6 pb-12 space-y-4 relative">
          <Card className="gap-5 relative">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-4">
                <div className="size-14 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <User className="size-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-xl">{customerName || t('account.yourAccount')}</CardTitle>
                  <div className="text-sm text-muted-foreground">
                    {t('account.refPrefix')} {account.external_account_ref}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {customerEmail ? (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="size-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{customerEmail}</span>
                </div>
              ) : null}
              {account.created_on && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="size-4 text-muted-foreground shrink-0" />
                  <div className="text-muted-foreground">
                    {t('account.createdPrefix')}{' '}
                    {new Date(account.created_on).toLocaleDateString(currentLanguage, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              )}
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('account.accountType')}</span>
                <Badge variant="subtle">{account.account_type}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
